import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ReleaseKitSummary } from '../../../../../redux/api/fymAPI.types';
import { songPickerMessages as m } from './songPickerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongPickerMadeProps {
  /** The kit summary of the song; undefined when nothing was made. */
  summary: ReleaseKitSummary | undefined;
}

function share(count: number, total: number): string {
  return `${Math.round((count / total) * 100)}%`;
}

/**
 * What is made for the song (design t1): a thin bar of ready, failed and working, and the line
 * "6 ready · 2 working · 1 failed", or "Nothing made yet".
 */
const SongPickerMadeComponent: React.FC<SongPickerMadeProps> = ({ summary }) => {
  const intl = useIntl();
  const ready = summary?.readyCount ?? 0;
  const working = summary?.workingCount ?? 0;
  const failed = summary?.failedCount ?? 0;
  const total = ready + working + failed;

  const text = useMemo(() => {
    if (total === 0) {
      return intl.formatMessage(m.madeNothing);
    }
    const parts: string[] = [];
    if (ready > 0) {
      parts.push(intl.formatMessage(m.madeReady, { count: ready }));
    }
    if (working > 0) {
      parts.push(intl.formatMessage(m.madeWorking, { count: working }));
    }
    if (failed > 0) {
      parts.push(intl.formatMessage(m.madeFailed, { count: failed }));
    }
    return parts.join(' · ');
  }, [failed, intl, ready, total, working]);

  return (
    <div className={styles.made}>
      <div className={styles.madeBar} aria-hidden="true">
        {total > 0 && (
          <>
            <i className={styles.madeReady} style={{ width: share(ready, total) }} />
            <i className={styles.madeFailed} style={{ width: share(failed, total) }} />
            <i className={styles.madeWorking} style={{ width: share(working, total) }} />
          </>
        )}
      </div>
      <span data-testid="song-picker-made">{text}</span>
    </div>
  );
};

export const SongPickerMade = TypedMemo(SongPickerMadeComponent);
